import { useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Table } from '@/components_deprecated'
import { format, parseISO } from 'date-fns'
import { ALL_HOLIDAY_ALLOWANCES_QUERY } from '@/modules/companies/queries'
import type { HolidayAllowanceNode } from '@/modules/companies/types.graphql'
import { useConfirm, useNodes } from '@/utils/hooks'
import { OldAddButton, Header, OldSection, SectionHeader } from '../common'
import type { HolidayAllowanceForm } from './HolidayAllowancePrompt'
import { HolidayAllowancePrompt } from './HolidayAllowancePrompt'
import { useHolidayAllowanceMutations } from './hooks'

export const HolidayAllowanceSettings: React.JSX.CFC = () => {
  const translations = useTranslate({
    prompt: {
      delete: ['settings.prompts.delete', { name: '' }],
      deleteTitle: 'settings.prompts.holiday-allowance-delete-title',
    },
    repeatAnnually: 'common.repeat-annually',
    salaryCode: 'common.salary-code',
    holidayAllowances: 'common.holiday-allowances',
    date: 'common.date',
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const mutations = useHolidayAllowanceMutations()

  const { data } = useQuery(ALL_HOLIDAY_ALLOWANCES_QUERY)
  const holidayAllowances = useNodes(data?.allHolidayAllowances)

  async function handleCreateHolidayAllowance() {
    const { data } = await addPrompt<HolidayAllowanceForm | null>(resolve => (
      <HolidayAllowancePrompt onSubmit={resolve} />
    ))
    if (data === null || data.salaryCodeId === null) return

    void mutations.createHolidayAllowance({
      variables: {
        input: {
          salaryCode: data.salaryCodeId,
          day: format(data.day, 'yyyy-MM-dd'),
          start: format(data.start, 'HH:mm'),
          end: format(data.end, 'HH:mm'),
          repeatAnnually: data.repeatAnnually,
        },
      },
    })
  }

  async function handlePatchHolidayAllowance(
    holidayAllowance: HolidayAllowanceNode
  ) {
    const { data } = await addPrompt<HolidayAllowanceForm | null>(resolve => (
      <HolidayAllowancePrompt
        initialData={holidayAllowance}
        onSubmit={resolve}
      />
    ))
    if (data === null || data.salaryCodeId === null) return

    void mutations.patchHolidayAllowance({
      variables: {
        id: holidayAllowance.id,
        input: {
          salaryCode: data.salaryCodeId,
          day: format(data.day, 'yyyy-MM-dd'),
          start: format(data.start, 'HH:mm'),
          end: format(data.end, 'HH:mm'),
          repeatAnnually: data.repeatAnnually,
        },
      },
    })
  }

  async function handleDeleteHolidayAllowance(
    holidayAllowance: HolidayAllowanceNode
  ) {
    const { data: answer } = await confirm(
      translations.prompt.delete({
        name:
          holidayAllowance.salaryCode?.name +
          ' ' +
          format(parseISO(holidayAllowance.day), 'dd.MM.yyyy'),
      }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.deleteHolidayAllowance({
      variables: {
        id: holidayAllowance.id,
      },
    })
  }

  return (
    <OldSection>
      <Header>
        <SectionHeader>{translations.holidayAllowances}</SectionHeader>
        <OldAddButton adding={false} onClick={handleCreateHolidayAllowance} />
      </Header>

      <Table
        noShadow
        noBorders
        noHeaderMargin
        noRowMargin
        rowHoverColor="secondary"
      >
        <thead>
          <tr>
            <th>{translations.date}</th>
            <th>{translations.salaryCode}</th>
            <th>{translations.repeatAnnually}</th>
            <th colSpan={2} />
          </tr>
        </thead>
        <tbody>
          {holidayAllowances.map(holidayAllowance => (
            <tr key={holidayAllowance.id}>
              <td>{format(parseISO(holidayAllowance.day), 'dd.MM.yyyy')}</td>
              <td>{holidayAllowance.salaryCode.name ?? '-'}</td>

              <td width="1px">
                {holidayAllowance.repeatAnnually ? (
                  <Icon icon="check" color="green" />
                ) : (
                  <Icon icon="times" color="red" />
                )}
              </td>

              <td width="1px">
                <Icon
                  icon="edit"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="secondary"
                  onClick={() => handlePatchHolidayAllowance(holidayAllowance)}
                />
              </td>

              <td width="1px">
                <Icon
                  icon="times"
                  cursor="pointer"
                  color="gray6"
                  hoverColor="red"
                  onClick={() => handleDeleteHolidayAllowance(holidayAllowance)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </OldSection>
  )
}
